import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Plus, UserPlus, RefreshCw, Upload, FileText, Loader2, Users, FolderPlus, Tags, ChevronDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import DataTable1 from '../components/ui/DataTable1';
import Dropdown from '../components/ui/Dropdown';
import { currencyCodeToSymbolMap, formatCurrency, fetchStoresWhereStaffAssigned, client, fetchGroups, fetchCustomerListV2 } from '../apiHelper';
import Input from '../components/ui/Input';
import TextArea from '../components/ui/TextArea';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Button from '../components/ui/Button';

const PAGE_SIZE = 20;

const CustomerManagement = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // --- State ---
    const [user, setUser] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [group, setGroups] = useState([])
    const [activeTab, setActiveTab] = useState('customers');

    const [loading, setLoading] = useState(false);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsHasMore, setGroupsHasMore] = useState(true);
    const [groupsPage, setGroupsPage] = useState(0);

    const [exporting, setExporting] = useState(false);
    const [hideZeroBalances, setHideZeroBalances] = useState(true);

    const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [assignedStores, setAssignedStores] = useState([]);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    const handleAttachLineOfCredit = useCallback((row) => {
        showToast(`Attaching line of credit for ${row.full_name || ''}`, 'info');
        navigate('/sales/customers/line-of-credit', {
            state: { customerId: row.customer_id, customerName: row.full_name?.trim(), customerEmail: row.email },
        });
    }, [navigate, showToast]);

    const handleViewCustomerReport = useCallback((row) => {
        showToast(`Viewing report for: ${row.full_name || ''}`, 'info');
        navigate('/reports/customer-report', {
            state: { customerId: row.customer_id, customerName: row.full_name?.trim(), customerEmail: row.email },
        });
    }, [navigate, showToast]);

    // --- Handlers ---
    // const handleAction = (key, row) => console.log(`Action: ${key}`, row);
    const handleAction = useCallback(
        (key, row) => {
            if (key === 'attach_line_of_credit') handleAttachLineOfCredit(row);
            else if (key === 'view_customer_report') handleViewCustomerReport(row);
            else if (key === 'export') showToast(`Exporting ${row.full_name}`, 'info');
        },
        [handleAttachLineOfCredit, handleViewCustomerReport, showToast]
    );

    const handleGroupRowAction = useCallback(
        (key, row) => {
            if (key === 'view_members') {
                navigate(
                    '/sales/group/members',
                    {
                        state: {
                            groupId: row.mbrgrp_id,
                            storeId: selectedStoreId,
                        }
                    }
                )
            }
        }
    )

    const handleRefresh = () => { 
        // setLoading(true); setTimeout(() => setLoading(false), 1000); 
        if(activeTab === 'customers') fetchCustomers();
        else refreshGroups();
    };
    const handleExportCSV = () => { setExporting(true); setTimeout(() => setExporting(false), 1000); };

    const openAddGroupModal = () => setIsAddGroupModalOpen(true);
    const closeAddGroupModal = () => {
        setIsAddGroupModalOpen(false);
        setGroupName('');
        setGroupDescription('');
        setSelectedStore('');
        setFormErrors({});
    }

    const handleManageGroups = () => navigate('/sales/customers/groups');

    useEffect(() => {
        const lcUser = localStorage.getItem('user');
        if (lcUser) {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);

            const loadInitialStores = async () => {
                try {
                    const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                    const mappedStores = fetchedStores.filter(s => s.storetype !== 'WHS').map(store => ({
                        label: store.identifier,
                        value: store.store_id,
                    }));
                    setAssignedStores(mappedStores);
                    if (mappedStores.length > 0) {
                        setSelectedStoreId(fetchedStores[0].store_id);
                    }
                } catch (err) {
                    showToast(err.response?.data?.detail || "Error loading assigned stores.", "error");
                }
            };
            loadInitialStores();
        } else {
            showToast("Please log in to manage customer data.", "info");
            navigate('/login');
        }
    }, [showToast, navigate]);

    useEffect(() => {
        if (isAddGroupModalOpen && user && stores.length === 0) {
            const load = async () => {
                setStoresLoading(true);
                try {
                    const data = await fetchStoresWhereStaffAssigned(user);
                    // console.log(data);
                    setStores(data.map((s) => ({ value: s.store_id, label: s.identifier })));
                } catch {
                    showToast('Failed to load stores.', 'error');
                } finally {
                    setStoresLoading(false);
                }
            };
            load();
        }
    }, [isAddGroupModalOpen, user, stores.length, showToast]);

    const handleAddGroup = async () => {
        const errors = {};
        if (!groupName.trim()) errors.groupName = 'Group name is required';
        if (!selectedStore) errors.store = 'Please select a branch';
        setFormErrors(errors);
        if (Object.keys(errors).length) return;

        setSaving(true);
        try {
            const payload = {
                groupName: groupName.trim(),
                groupDescription: groupDescription.trim() || null,
                store_id: selectedStore,
                owner_id: user.users_id,
            }
            const response = await client.postData('/users/create-group', payload);

            const newGroup = {
                ...response,
                member_count: 0,
                created_at: new Date().toISOString(),
                stores: stores.find((s) => s.value === selectedStore)
                    ? [{ store_id: selectedStore, identifier: stores.find((s) => s.value === selectedStore).label }]
                    : [],
            };
            // console.log(newGroup)
            setGroups((prev) => [newGroup, ...prev]);
            showToast('Group created successfully', 'success');
            closeAddGroupModal();
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to create group', 'error');
        } finally {
            setSaving(false);
        }
    }

    const fetchCustomerGroups = useCallback(
        async (reset = false) => {
            if (!user || groupsLoading || !selectedStoreId) return;
            const offset = reset ? 0 : groupsPage * PAGE_SIZE;
            setGroupsLoading(true);
            try {
                const data = await fetchGroups({ 
                    offset, 
                    limit: PAGE_SIZE,
                    store_id: selectedStoreId, 
                });
                // console.log(data)
                if (reset) {
                    setGroups(data);
                    setGroupsPage(1);
                } else {
                    setGroups((prev) => [...prev, ...data]);
                    setGroupsPage((p) => p + 1);
                }
                setGroupsHasMore(data.length === PAGE_SIZE);
            } catch (err) {
                // console.error(err)
                showToast(err.response?.data?.detail || 'Failed to load groups.', 'error')
                setGroupsHasMore(false);
            } finally {
                setGroupsLoading(false);
            }
        },
        [user, groupsLoading, showToast, selectedStoreId]
    );

    const refreshGroups = () => {
        if(!selectedStoreId) {
            showToast("Please select a branch first", "info");
            return;
        }

        setGroups([]);
        setGroupsPage(0);
        setGroupsHasMore(true);
        fetchCustomerGroups(true);
    };

    const loadMoreGroups = () => {
        if (groupsHasMore && !groupsLoading) fetchCustomerGroups();
    }

    const handleImportClick = (target) => {
        navigate('/import-data', { state: { target } });
    };

    const groupColumns = [
        {
            key: 'name',
            label: 'Group Name',
            render: (val, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                        <Tags size={16} />
                    </div>
                    <div>
                        <div className="font-semibold text-gray-900">{val}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]">{row.description}</div>
                    </div>
                </div>
            )
        },
        {
            key: 'stores',
            label: 'Assigned Branch',
            render: (stores) => (
                <span className="text-sm text-gray-600">
                    {stores?.[0]?.identifier || 'Universal'}
                </span>
            )
        },
        {
            key: 'member_count',
            label: 'Members',
            render: (val) => (
                <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
                        {val || 0} Members
                    </span>
                </div>
            )
        },
        {
            key: 'mbrgrp_id',
            label: 'Group ID',
            render: (val) => <span className="font-mono text-xs text-gray-400">#{val}</span>
        }
    ];

    const fetchCustomers = useCallback(async () => {
        if (!user || !selectedStoreId) return;

        setLoading(true);
        try {
            const data =await fetchCustomerListV2(selectedStoreId);
            // console.log(data)
            setCustomers(data);
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to load customers.', 'error');
        } finally {
            setLoading(false);
        }
    }, [user, selectedStoreId, showToast]);


    // useEffect(() => {
    //     if (user && activeTab === 'customers' && selectedStoreId) fetchCustomers();
    //     if (user && activeTab === 'group' && selectedStoreId) {
    //         fetchCustomerGroups(true)
    //     }
    // }, [user, activeTab, fetchCustomerGroups, fetchCustomers, selectedStoreId]);

    useEffect(() => {
        if (!user || !selectedStoreId) return;

        if (activeTab === 'customers') {
            fetchCustomers();
        } else if (activeTab === 'group') {
            fetchCustomerGroups(true);
        }
    },[activeTab, selectedStoreId, user]);

    const columns = [
        {
            key: 'display_id',
            label: 'Customer ID',
            render: (val, row) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-xs uppercase shrink-0">
                        {row.first_name?.[0] || '?'}{row.last_name?.[0] || ''}
                    </div>
                    <span className="font-mono text-xs font-medium text-gray-500">{val}</span>
                </div>
            )
        },
        { key: 'full_name', label: 'Name' },
        // { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        {
            key: 'credit_limit',
            label: 'Credit Limit',
            // you should import formatCurrency from apiHelper and pass the args (amount, decimals = 2, currency = '')
            // currency is in the data 
            render: (val, row) => (
                <span className="font-semibold text-gray-800">
                    {formatCurrency(val, 2, row.currency, currencyCodeToSymbolMap)}
                </span>
            )
        },
        {
            key: 'receivable_balance',
            label: 'Balance',
            // you should import formatCurrency from apiHelper and pass the args (amount, decimals = 2, currency = '')
            // currency is in the data 
            render: (val, row) => (
                <span className={`font-medium ${Number(val) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(val, 2, row.currency, currencyCodeToSymbolMap)}
                </span>
            )
        },
        {
            key: 'latest_order_amount',
            label: 'Last Invoice',
            render: (val, row) => (
                <div className="text-xs">
                    <div className="font-medium text-gray-900">{val ? formatCurrency(val, 2, row.currency) : 'N/A'}</div>
                    <div className="text-gray-400">{row.latest_order_date?.split(' ')[0] || ''}</div>
                </div>
            )
        }, // this should combine last_order_amount + last_order_date
        {
            key: 'last_payment',
            label: 'Last Payment',
            render: (val, row) => (
                <span className="text-sm text-gray-600">
                    {val ? formatCurrency(val, 2, row.currency) : '-'}
                </span>
            )
        }, // last_payment_amount
        { key: 'store_identifier', label: 'Branch' },  // store_identifier
    ];

    // --- Actions ---
    const rowActions = [
        { label: 'View Report', key: 'view_customer_report' },
        { label: 'Export Row', key: 'export' },
        { label: 'Manage Line of Credit', key: 'attach_line_of_credit' }
    ];


    const displayData = useMemo(() =>
        hideZeroBalances ? customers.filter(c => Number(c.receivable_balance) > 0) : customers,
        [hideZeroBalances, customers]);


    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-sans">
            {/* Removed max-w-7xl to restore full-width layout */}
            <div className="w-full space-y-6">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-50 rounded-lg text-purple-600">
                            <Users size={24} />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Customer Data</h1>
                            <p className="text-sm text-gray-500">Comprehensive view of member hierarchy and credit standings.</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="w-48">
                            <SearchableSelectBox
                                name="filterStore"
                                placeholder="Switch Branch"
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                options={assignedStores}
                                loading={loading}
                                containerClassName='!mb-0'
                            />
                        </div>
                        {/* Group Operations */}
                        <Dropdown
                            trigger={
                                <button className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
                                    <Tags size={18} className="text-purple-500" />
                                    <span className="hidden sm:inline font-medium">Groups</span>
                                    <ChevronDown size={14} className="text-gray-400" />
                                </button>
                            }
                            align="right"
                        >
                            <div className="py-1 w-52 text-sm">
                                <button onClick={openAddGroupModal} className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-3">
                                    <FolderPlus size={16} className="text-gray-500" /> Create New Group
                                </button>
                                <button onClick={() => handleImportClick('customer-groups')} className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-3">
                                    <Upload size={16} className="text-gray-500" /> Import Groups
                                </button>
                                <button onClick={() => handleImportClick('grouped-customers')} className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-3">
                                    <Upload size={18} className="text-gray-500" />Import Grouped Customers
                                </button>
                                <div className="border-t border-gray-100 my-1"></div>
                                {/* <button onClick={handleManageGroups} className="w-full text-left px-4 py-2.5 text-purple-600 font-medium hover:bg-purple-50 flex items-center gap-3">
                                    <Tags size={16} /> View All Groups
                                </button> */}
                            </div>
                        </Dropdown>

                        {/* Refresh */}
                        <button onClick={fetchCustomers} className="flex items-center justify-center p-2.5 sm:px-4 sm:py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                            <RefreshCw className={loading ? 'animate-spin' : ''} size={18} />
                            <span className="hidden md:inline ml-2 font-medium">Refresh</span>
                        </button>

                        {/* New Customer */}
                        <Dropdown
                            trigger={
                                <button className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors shadow-sm">
                                    <Plus size={18} /> <span className="hidden sm:inline">New Customer</span>
                                </button>
                            }
                            align="right"
                        >
                            <div className="py-1 w-48 text-sm">
                                <button onClick={() => navigate('/sales/customers/new')} className="w-full text-left sm:px-4 sm:py-2 rounded-lg p-2.5 hover:bg-gray-100 flex items-center gap-3">
                                    <UserPlus size={16} /> New Customer
                                </button>
                                <button onClick={() => handleImportClick('customers')} className="w-full text-left px-4 py-2.5 hover:bg-gray-100 flex items-center gap-3 border-t border-gray-200">
                                    <Upload size={16} /> Import Customers
                                </button>
                            </div>
                        </Dropdown>

                        {/* Hide Zero Toggle */}
                        <label className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500"
                                checked={hideZeroBalances}
                                onChange={(e) => setHideZeroBalances(e.target.checked)}
                            />
                            <span className="text-xs font-bold text-gray-600 uppercase tracking-tight">Hide Zero</span>
                        </label>

                        {/* Quick Exports */}
                        <div className="flex items-center gap-2">
                            <button onClick={handleExportCSV} className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm transition-colors">
                                <Upload size={18} className="rotate-180" />
                            </button>
                            <button className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-sm transition-colors">
                                <FileText size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex border-b border-gray-200 mb-4">
                    <button
                        onClick={() => setActiveTab('customers')}
                        className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'customers'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Customer Ledger
                    </button>
                    <button
                        onClick={() => setActiveTab('group')}
                        className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'group'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Member Groups ({group.length})
                    </button>
                </div>
                {activeTab === 'customers' ? (
                    <DataTable1
                        data={displayData}
                        columns={columns}
                        loading={loading}
                        enableSearch={true}
                        enablePagination={true}
                        rowsPerPage={10}
                        onAction={handleAction}
                        rowActions={rowActions}
                    />
                ) : (
                    <DataTable1
                        data={group}
                        columns={groupColumns}
                        loading={groupsLoading}
                        enableSearch={true}
                        enablePagination={true}
                        rowsPerPage={10}
                        emptyMessage="No customer groups found. Create one to get started."
                        onAction={handleGroupRowAction}
                        rowActions={[
                            { label: 'View Group Members', key: 'view_members' },
                            { label: 'Edit Details', key: 'edit_group' },
                            { label: 'Delete Group', key: 'delete_group' }
                        ]}
                    />
                )}
            </div>
            {isAddGroupModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-xl font-semibold">Add New Customer Group</h3>
                            <button onClick={closeAddGroupModal} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <Input
                                label="Group Name"
                                placeholder="e.g. VIP Customers, Class One"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                required
                                error={formErrors.groupName}
                            />
                            <TextArea
                                label="Description (optional)"
                                placeholder="Brief description..."
                                rows={4}
                                value={groupDescription}
                                onChange={(e) => setGroupDescription(e.target.value)}
                            />
                            <SearchableSelectBox
                                name="store"
                                label="Branch"
                                placeholder="Search branches..."
                                value={selectedStore}
                                onChange={(e) => setSelectedStore(e.target.value)}
                                options={stores}
                                loading={storesLoading}
                                required
                                error={formErrors.store}
                            />
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t">
                            <button
                                onClick={closeAddGroupModal}
                                disabled={saving}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddGroup}
                                disabled={saving}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving && <Loader2 size={18} className="animate-spin" />}
                                {saving ? 'Creating...' : 'Create Group'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerManagement;